import { useState } from 'react';
import { formatQuizAnswers, isQuizAnswered, sanitizeQuizAnswers, type Quiz, type QuizAnswers } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { hapticFeedback } from '../lib/haptics.js';
import { loadQuizAnswers, saveQuizAnswers } from '../lib/quizStore.js';

export interface QuizCardProps {
  quiz: Quiz;
  messageId: string;
  /** Can still be answered: the latest finished reply. Older cards are read-only. */
  answerable: boolean;
  /** Sends the formatted answers as the next user message. */
  onSubmit?: (text: string) => void;
}

/** Clarifying questions with tappable options; the answers go back as the next message. */
export function QuizCard({ quiz, messageId, answerable, onSubmit }: QuizCardProps) {
  const { t } = useI18n();
  const [sent, setSent] = useState<QuizAnswers | null>(() => loadQuizAnswers(messageId));
  const [draft, setDraft] = useState<QuizAnswers>({});
  const answers = sent ?? draft;
  const locked = sent !== null || !answerable || !onSubmit;

  function toggle(questionId: string, option: string, multiple: boolean) {
    if (locked) return;
    hapticFeedback('select');
    setDraft((prev) => {
      const current = prev[questionId] ?? [];
      if (!multiple) return { ...prev, [questionId]: [option] };
      return {
        ...prev,
        [questionId]: current.includes(option) ? current.filter((item) => item !== option) : [...current, option],
      };
    });
  }

  function submit() {
    if (locked || !isQuizAnswered(quiz, draft)) return;
    const clean = sanitizeQuizAnswers(quiz, draft);
    const text = formatQuizAnswers(quiz, clean, t('quiz.answersIntro'));
    saveQuizAnswers(messageId, clean);
    setSent(clean);
    hapticFeedback('tap');
    onSubmit?.(text);
  }

  return (
    <form
      className={`quiz-card${locked ? ' is-locked' : ''}`}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {quiz.questions.map((question, index) => (
        <fieldset key={question.id} className="quiz-question" disabled={locked}>
          <legend className="quiz-question-text">
            {index + 1}. {question.text}
            {question.multiple && <span className="quiz-multiple-hint"> · {t('quiz.multipleHint')}</span>}
          </legend>
          <div className="quiz-options">
            {question.options.map((option) => {
              const checked = (answers[question.id] ?? []).includes(option);
              return (
                <label key={option} className={`quiz-option${checked ? ' is-checked' : ''}`}>
                  <input
                    type={question.multiple ? 'checkbox' : 'radio'}
                    name={`${messageId}-${question.id}`}
                    checked={checked}
                    onChange={() => toggle(question.id, option, question.multiple)}
                  />
                  <span className={`quiz-mark${question.multiple ? ' is-square' : ''}`} aria-hidden="true" />
                  <span className="quiz-option-text">{option}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
      {sent ? (
        <p className="quiz-sent" role="status">
          {t('quiz.sent')}
        </p>
      ) : answerable && onSubmit ? (
        <button type="submit" className="quiz-submit" disabled={!isQuizAnswered(quiz, draft)}>
          {quiz.submitLabel ?? t('quiz.submit')}
        </button>
      ) : null}
    </form>
  );
}
